import { motion } from "framer-motion";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/Button";
import { LogoFull } from "./Logo";
import { profiles } from "../data/profiles";
import type { Profile } from "../data/questions";
import { generateShareCardBlob } from "./ShareCard";
import { trackEvent } from "../lib/analytics";
import { appendUtm } from "../lib/utm";

interface ResultScreenProps {
  profile: Profile;
  percentage: number;
  onRestart: () => void;
}

const QUIZ_URL = import.meta.env.VITE_QUIZ_PUBLIC_URL || "https://quiz.ommistack.com";
const OMMISTACK_URL = import.meta.env.VITE_OMMISTACK_URL || "https://ommistack.com";

export function ResultScreen({ profile, percentage, onRestart }: ResultScreenProps) {
  const profileData = profiles[profile];
  const [sharing, setSharing] = useState(false);

  const ommistackHref = appendUtm(OMMISTACK_URL, {
    utm_source: "quiz",
    utm_medium: "result",
    utm_campaign: "quel_investisseur",
    utm_content: profile,
  });

  const shareUrl = `${QUIZ_URL}?ref=${profile}`;
  const shareText = `Et toi, quel investisseur es-tu ? 🚀\n${shareUrl}`;

  const handleShare = async () => {
    setSharing(true);
    trackEvent("share_click", { profile });
    try {
      const blob = await generateShareCardBlob(profileData, percentage);
      if (!blob) throw new Error("blob null");
      const file = new File([blob], "mon-profil-investisseur.png", { type: "image/png" });

      // Web Share API avec fichier
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          text: shareText,
          files: [file],
        });
        trackEvent("share_native_success", { profile });
      } else {
        // Fallback : download + copy URL
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "mon-profil-investisseur.png";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        try {
          await navigator.clipboard.writeText(shareUrl);
          toast.success("Image téléchargée 📥", {
            description: "Lien du quiz copié dans le presse-papier.",
          });
        } catch {
          toast.success("Image téléchargée 📥");
        }
      }
    } catch (err) {
      console.warn("share error", err);
      toast.error("Partage indisponible", { description: "Essaie de copier le lien à la place." });
    } finally {
      setSharing(false);
    }
  };

  const handleCopyLink = async () => {
    trackEvent("copy_link", { profile });
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Lien copié ✓");
    } catch {
      toast.error("Copie impossible — copie manuellement.");
    }
  };

  const handleTweet = () => {
    trackEvent("share_twitter", { profile });
    const url =
      "https://twitter.com/intent/tweet?text=" +
      encodeURIComponent(`${profileData.punchline} 🚀`) +
      "&url=" +
      encodeURIComponent(shareUrl);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="min-h-[100svh] bg-background pb-24">
      <div className="max-w-md mx-auto px-6 py-8">
        <div className="flex justify-center mb-6 opacity-80">
          <LogoFull />
        </div>

        {/* Carte profil principale */}
        <motion.div
          initial={{ scale: 0, rotate: -10, opacity: 0 }}
          animate={{ scale: 1, rotate: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 130, damping: 12 }}
          className="rounded-2xl gradient-jade text-white px-8 py-7 shadow-soft text-center"
        >
          <div className="text-5xl mb-2" aria-hidden>{profileData.emoji}</div>
          <h1 className="font-heading font-black text-3xl">{profileData.name}</h1>
          <p className="font-heading font-bold text-lg opacity-85 mt-1">
            {percentage}% match
          </p>
        </motion.div>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-5 text-center italic font-body text-foreground/80"
        >
          « {profileData.tagline} »
        </motion.p>

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-6 rounded-2xl bg-white p-6 shadow-card space-y-4 text-foreground/90 font-body leading-relaxed"
        >
          {profileData.description.split("\n\n").map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </motion.div>

        {/* Traits */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-5 rounded-2xl bg-white p-6 shadow-card"
        >
          <h3 className="font-heading font-bold text-lg mb-3">Tes traits :</h3>
          <ul className="space-y-2">
            {profileData.traits.map((t, i) => (
              <li key={i} className="flex gap-3 text-foreground/90 font-body">
                <span className="text-primary font-bold mt-0.5" aria-hidden>✓</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Conseils */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-5 rounded-2xl bg-white p-6 shadow-card"
        >
          <h3 className="font-heading font-bold text-lg mb-3">Nos conseils 💡</h3>
          <ul className="space-y-2">
            {profileData.conseils.map((c, i) => (
              <li key={i} className="flex gap-3 text-foreground/90 font-body">
                <span className="text-tangerine font-bold mt-0.5" aria-hidden>→</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Section partage */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-6 rounded-2xl bg-white p-6 shadow-card text-center"
        >
          <h3 className="font-heading font-bold text-lg mb-2">Partage ton profil :</h3>
          <p className="italic text-foreground/80 mb-5">{profileData.punchline}</p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleShare}
              loading={sharing}
              className="gradient-jade text-white border-none w-full rounded-2xl"
              size="lg"
            >
              Partager 🔗
            </Button>
            <Button
              variant="secondary"
              onClick={handleCopyLink}
              className="w-full rounded-2xl"
            >
              Copier le lien du quiz 📋
            </Button>
            <button
              type="button"
              onClick={handleTweet}
              className="text-sm font-heading text-muted-foreground hover:text-primary transition-colors no-tap-highlight"
            >
              Tweeter 🐦
            </button>
          </div>
        </motion.div>

        {/* CTA Ommistack */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mt-6 rounded-2xl bg-white p-6 border-2 border-jade/40 shadow-soft"
        >
          <p className="font-body text-foreground/90 mb-4">{profileData.nextStep}</p>
          <a
            href={ommistackHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 font-heading font-bold text-base text-white shadow-cta no-tap-highlight transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: "#396742" }}
            onClick={() => trackEvent("cta_ommistack_click", { profile })}
          >
            {profileData.cta}
          </a>
        </motion.div>

        <button
          type="button"
          onClick={onRestart}
          className="mt-8 w-full text-sm font-heading text-muted-foreground hover:text-primary transition-colors no-tap-highlight"
        >
          Refaire le quiz
        </button>
      </div>
    </section>
  );
}
